import { Button, Input, Modal, Select, Table, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import { saveAs } from "file-saver";
import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../../nestserver";

async function fetchGeneratedCredentials() {
  const res = await axios({
    method: "get",
    url: `${serverUrl}/generated-credentials`,
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
    },
  });

  return res.data;
}

async function fetchClasses() {
  const res = await axios({
    method: "get",
    url: `${serverUrl}/classes`,
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
    },
  });

  return res.data;
}

async function generateLegacyCredentials(body) {
  const res = await axios({
    method: "post",
    url: `${serverUrl}/generated-credentials/generate-legacy`,
    data: body,
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
    },
  });

  return res.data;
}

// Users created before credentials were recorded only have a hashed password,
// which can't be shown — the admin can generate a new one instead.
const notAvailable = <span className="text-gray-400">Not available</span>;

const instructorColumns = [
  {
    title: "Instructor Name",
    dataIndex: "instructor_name",
    key: "instructor_name",
  },
  { title: "Email", dataIndex: "email", key: "email" },
  { title: "EMPID", dataIndex: "empid", key: "empid" },
  { title: "Password", dataIndex: "password", key: "password" },
];

function UserCredentialsSettings() {
  const { data, isLoading } = useQuery(
    ["generated-credentials"],
    fetchGeneratedCredentials
  );
  const classes = useQuery(["classes"], fetchClasses);
  const queryClient = useQueryClient();

  const [studentSearch, setStudentSearch] = useState("");
  const [classId, setClassId] = useState(null);
  const [instructorSearch, setInstructorSearch] = useState("");
  const [exportingStudents, setExportingStudents] = useState(false);
  const [exportingInstructors, setExportingInstructors] = useState(false);
  const [generating, setGenerating] = useState(false);

  function confirmGenerate(body, title) {
    Modal.confirm({
      title,
      content:
        "This sets a new password. Anyone currently logging in with their old password will need the new one.",
      okText: "Generate",
      onOk: async () => {
        setGenerating(true);
        try {
          const result = await generateLegacyCredentials(body);
          message.success(
            `Generated ${result.students} student and ${result.parents} parent password(s).`
          );
          if (result.failed) {
            message.warning(`${result.failed} could not be generated.`);
          }
          await queryClient.invalidateQueries(["generated-credentials"]);
        } catch (err) {
          message.error("Could not generate passwords. Please try again.");
        } finally {
          setGenerating(false);
        }
      },
    });
  }

  const studentColumns = [
    { title: "Student Name", dataIndex: "student_name", key: "student_name" },
    { title: "Parent Name", dataIndex: "parent_name", key: "parent_name" },
    { title: "STDID", dataIndex: "stdid", key: "stdid" },
    {
      title: "STD Password",
      dataIndex: "std_password",
      key: "std_password",
      render: (value, r) => (r.std_has_password ? value : notAvailable),
    },
    {
      title: "Parent Username",
      dataIndex: "parent_username",
      key: "parent_username",
    },
    // { title: "Parent Email", dataIndex: "parent_email", key: "parent_email" },
    {
      title: "Parent Password",
      dataIndex: "parent_password",
      key: "parent_password",
      render: (value, r) =>
        r.parent_has_password ? value : r.parent_name ? notAvailable : "",
    },
    { title: "Class", dataIndex: "class_name", key: "class_name" },
    {
      title: "",
      key: "generate",
      render: (_, r) =>
        r.can_generate && (
          <Button
            size="small"
            onClick={() =>
              confirmGenerate(
                { studentIds: [r.student_id] },
                `Generate new passwords for ${r.student_name} and parent?`
              )
            }
          >
            Generate
          </Button>
        ),
    },
  ];

  const filteredStudents = useMemo(() => {
    const term = studentSearch.trim().toLowerCase();
    return (data?.students ?? []).filter((r) => {
      const matchesSearch =
        !term ||
        r.student_name?.toLowerCase().includes(term) ||
        r.parent_name?.toLowerCase().includes(term);
      const matchesClass = !classId || r.class_id === classId;
      return matchesSearch && matchesClass;
    });
  }, [data, studentSearch, classId]);

  const filteredInstructors = useMemo(() => {
    const term = instructorSearch.trim().toLowerCase();
    return (data?.instructors ?? []).filter(
      (r) => !term || r.instructor_name?.toLowerCase().includes(term)
    );
  }, [data, instructorSearch]);

  async function handleExportStudentParentPdf() {
    setExportingStudents(true);
    try {
      const response = await axios({
        url: `${serverUrl}/generated-credentials/export/student-parent/pdf`,
        method: "GET",
        responseType: "blob",
        params: {
          ...(studentSearch && { search: studentSearch }),
          ...(classId && { classId }),
        },
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      saveAs(response.data, "student-parent-credentials.pdf");
    } catch (err) {
      message.error("Export failed. Please try again.");
    } finally {
      setExportingStudents(false);
    }
  }

  async function handleExportInstructorPdf() {
    setExportingInstructors(true);
    try {
      const response = await axios({
        url: `${serverUrl}/generated-credentials/export/instructor/pdf`,
        method: "GET",
        responseType: "blob",
        params: {
          ...(instructorSearch && { search: instructorSearch }),
        },
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      saveAs(response.data, "instructor-credentials.pdf");
    } catch (err) {
      message.error("Export failed. Please try again.");
    } finally {
      setExportingInstructors(false);
    }
  }

  return (
    <>
      <div className="mt-8">
        <div className="bg-white drop-shadow-md p-8 w-full rounded-lg">
          <div className="flex items-center justify-between py-4">
            <div className="text-2xl text-orange-500 font-semibold">
              Student &amp; Parent Credentials
            </div>
            <div className="flex space-x-3">
              <Button
                loading={generating}
                disabled={
                  !classId || !filteredStudents.some((r) => r.can_generate)
                }
                onClick={() =>
                  confirmGenerate(
                    { classId },
                    "Generate passwords for all students in this class who don't have one?"
                  )
                }
              >
                Generate for class
              </Button>
              <Button
                loading={exportingStudents}
                onClick={handleExportStudentParentPdf}
              >
                Export PDF
              </Button>
            </div>
          </div>
          <div className="flex space-x-3 mb-3">
            <Input
              placeholder="Search by student or parent name"
              allowClear
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              prefix={<SearchOutlined className="text-gray-400" />}
              style={{ maxWidth: 320 }}
            />
            <Select
              placeholder="Filter by class"
              allowClear
              style={{ minWidth: 200 }}
              value={classId}
              onChange={(value) => setClassId(value ?? null)}
              options={(classes.data ?? []).map((c) => ({
                value: c._id,
                label: c.name,
              }))}
            />
          </div>
          <div className="py-5">
            <Table
              rowKey={(record, index) => index}
              columns={studentColumns}
              dataSource={filteredStudents}
              loading={isLoading}
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="bg-white drop-shadow-md p-8 w-full rounded-lg">
          <div className="flex items-center justify-between py-4">
            <div className="text-2xl text-orange-500 font-semibold">
              Instructor Credentials
            </div>
            <Button
              loading={exportingInstructors}
              onClick={handleExportInstructorPdf}
            >
              Export PDF
            </Button>
          </div>
          <div className="mb-3">
            <Input
              placeholder="Search by instructor name"
              allowClear
              value={instructorSearch}
              onChange={(e) => setInstructorSearch(e.target.value)}
              prefix={<SearchOutlined className="text-gray-400" />}
              style={{ maxWidth: 320 }}
            />
          </div>
          <div className="py-5">
            <Table
              rowKey={(record, index) => index}
              columns={instructorColumns}
              dataSource={filteredInstructors}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default UserCredentialsSettings;
